import React, { useRef, useEffect } from 'react';
import { Box, styled } from '@mui/material';

interface ResizablePanelProps {
  children: React.ReactNode;
  width: number;
  height: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
  resizeHandlePosition?: 'left' | 'right';
}

const Panel = styled(Box)<{ width: number }>(({ width }) => ({
  position: 'relative',
  width: `${width}px`,
  height: '100%',
  overflow: 'hidden',
  backgroundColor: '#1e1e1e',
}));

const ResizeHandle = styled('div')<{ position: 'left' | 'right' }>(({ position }) => ({
  position: 'absolute',
  [position]: 0,
  top: 0,
  width: '4px',
  height: '100%',
  cursor: 'col-resize',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  },
  '&:active': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)'
  },
  zIndex: 10,
}));

const ResizablePanel: React.FC<ResizablePanelProps> = ({
  children,
  width,
  height,
  minWidth = 200,
  maxWidth = 600,
  onWidthChange,
  resizeHandlePosition = 'right'
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const resize = (e: MouseEvent) => {
      if (!isResizing.current) return;

      const delta = e.pageX - startX.current;
      let newWidth = resizeHandlePosition === 'right'
        ? startWidth.current + delta
        : startWidth.current - delta;

      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      
      if (onWidthChange) {
        onWidthChange(newWidth);
      }
    };

    const stopResize = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    };

    const startResize = (e: MouseEvent) => {
      e.preventDefault();
      isResizing.current = true;
      startX.current = e.pageX;
      startWidth.current = panel.offsetWidth;

      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);
    };

    const handle = panel.querySelector('.resize-handle') as HTMLElement;
    if (handle) {
      handle.addEventListener('mousedown', startResize);
    }

    return () => {
      if (handle) {
        handle.removeEventListener('mousedown', startResize);
      }
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    };
  }, [minWidth, maxWidth, onWidthChange, resizeHandlePosition]);

  return (
    <Panel ref={panelRef} width={width}>
      <ResizeHandle
        className="resize-handle"
        position={resizeHandlePosition}
      />
      {children}
    </Panel>
  );
};

export default ResizablePanel;
